import {
  type AssessmentDimensionData,
  RECOMMENDATION_COLORS,
  RECOMMENDATION_LABELS,
} from "@components/campaigns/CandidateAssessmentSummary";
import { RadarChart } from "@components/charts/RadarChart";
import { HelpLink } from "@components/help/HelpLink";
import { HowDeterminedButton } from "@components/provenance/HowDeterminedButton";

interface CompetencyScoreData {
  id: string;
  score: number | null;
  anchorLabel?: string | null;
  evidence: string | null;
  notes: string | null;
  detailedNotes: string | null;
  capabilityDimension: {
    id: string;
    continuityId: string;
    name: string;
    description: string | null;
    idealScore: number | null;
  };
}

interface StructuralFitScoreData {
  id: string;
  briefFieldKey: string;
  briefFieldLabel: string;
  briefFieldValueExcerpt: string | null;
  score: number | null;
  anchorLabel?: string | null;
  evidence: string | null;
}

interface StructuralFitDimensionCoverageData {
  briefFieldKey: string;
  briefFieldLabel: string;
  coveringDimensions: Array<{ id: string; continuityId: string; name: string }>;
}

export interface GenerationRunData {
  id: string;
  inputCount: number;
  modelUsed: string | null;
  finishedAt: string | null;
}

export interface CandidateAssessmentDetailData {
  overallScore: number | null;
  recommendation: string | null;
  overallSummary: string | null;
  strengths: string | null;
  concerns: string | null;
  versionNumber: number;
  effectiveFrom: string;
  assessor: { id: string; name: string };
  generationRun?: GenerationRunData | null;
  competencyScores: CompetencyScoreData[];
  structuralFitScores?: StructuralFitScoreData[];
  structuralFitOverallScore?: number | null;
  structuralFitDimensionCoverage?: StructuralFitDimensionCoverageData[];
}

interface CandidateAssessmentDetailProps {
  assessment: CandidateAssessmentDetailData;
  candidateName?: string;
  // Enables the "How was this determined?" provenance panel; the button only
  // renders when the surface can say which campaign candidate this is.
  campaignCandidateId?: string;
  dimensions?: AssessmentDimensionData[];
  onViewDetails?: () => void;
}

export function CandidateAssessmentDetail({
  assessment,
  candidateName,
  campaignCandidateId,
  dimensions,
  onViewDetails,
}: CandidateAssessmentDetailProps) {
  const showRadar =
    dimensions != null && dimensions.length > 0 && assessment.competencyScores.length > 0;
  const scoresMap: Record<string, number> = {};
  if (showRadar) {
    for (const cs of assessment.competencyScores) {
      if (cs.score != null) scoresMap[cs.capabilityDimension.continuityId] = cs.score;
    }
  }

  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        Assessment
      </h4>
      <div className="space-y-3 rounded border border-border p-3">
        <p className="text-[11px] text-text-faint">
          Assessed by {assessment.assessor.name} &middot; v{assessment.versionNumber} &middot;{" "}
          {new Date(assessment.effectiveFrom).toLocaleDateString()}
        </p>

        {assessment.generationRun && (
          <p className="text-[11px] text-text-faint" data-testid="assessment-generation-run-line">
            Generated from {assessment.generationRun.inputCount}{" "}
            {assessment.generationRun.inputCount === 1 ? "input" : "inputs"}
            {assessment.generationRun.modelUsed && (
              <> &middot; {assessment.generationRun.modelUsed}</>
            )}
            {assessment.generationRun.finishedAt && (
              <> &middot; {new Date(assessment.generationRun.finishedAt).toLocaleString()}</>
            )}
          </p>
        )}

        {campaignCandidateId && (
          <HowDeterminedButton
            campaignCandidateId={campaignCandidateId}
            candidateName={candidateName}
            subject={{ kind: "assessment" }}
          />
        )}

        <div className="flex items-center gap-3">
          {assessment.overallScore != null && (
            <span className="text-sm font-semibold text-text-primary">
              {assessment.overallScore}/5
            </span>
          )}
          {assessment.recommendation && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${RECOMMENDATION_COLORS[assessment.recommendation] ?? ""}`}
            >
              {RECOMMENDATION_LABELS[assessment.recommendation] ?? assessment.recommendation}
            </span>
          )}
        </div>

        {assessment.overallSummary && (
          <p className="whitespace-pre-wrap text-xs text-text-secondary">
            {assessment.overallSummary}
          </p>
        )}

        {showRadar && (
          <div className="flex justify-center">
            <RadarChart
              dimensions={dimensions}
              candidateScores={scoresMap}
              candidateName={candidateName ?? "Candidate"}
              size="sm"
            />
          </div>
        )}

        {assessment.competencyScores.length > 0 && (
          <div className="space-y-2">
            <h5 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Competency Scores
              <HelpLink to="/help/anchors#what-is-an-anchor" label="Guide: behavioural anchors" />
            </h5>
            <div className="space-y-2">
              {assessment.competencyScores.map((cs) => (
                <div key={cs.id} className="rounded border border-border/60 p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-text-primary">
                      {cs.capabilityDimension.name}
                    </span>
                    <span className="flex items-center gap-2 text-[10px] text-text-faint">
                      {cs.score != null ? (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-text-secondary dark:bg-gray-800">
                          {cs.score}/5
                        </span>
                      ) : (
                        <span>unscored</span>
                      )}
                      {cs.capabilityDimension.idealScore != null && (
                        <span>required: {cs.capabilityDimension.idealScore}/10</span>
                      )}
                    </span>
                  </div>
                  {cs.evidence && (
                    <p className="mt-1 whitespace-pre-wrap text-[11px] text-text-secondary">
                      {cs.evidence}
                    </p>
                  )}
                  {cs.notes && (
                    <p className="mt-1 whitespace-pre-wrap text-[11px] italic text-text-faint">
                      {cs.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {((assessment.structuralFitScores && assessment.structuralFitScores.length > 0) ||
          (assessment.structuralFitDimensionCoverage &&
            assessment.structuralFitDimensionCoverage.length > 0)) && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h5 className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                Structural fit
              </h5>
              {assessment.structuralFitOverallScore != null && (
                <span className="text-[10px] text-text-faint">
                  Overall:{" "}
                  <span className="font-medium text-text-secondary">
                    {assessment.structuralFitOverallScore}/5
                  </span>
                </span>
              )}
            </div>
            <p className="text-[10px] text-text-faint">
              How populated brief fields are scored against this candidate.
            </p>
            <div className="space-y-2">
              {assessment.structuralFitScores?.map((sf) => (
                <div key={sf.id} className="rounded border border-border/60 p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="text-xs font-medium text-text-primary"
                      title={`Brief field: ${sf.briefFieldKey}`}
                    >
                      {sf.briefFieldLabel}
                    </span>
                    <span className="flex items-center gap-2 text-[10px] text-text-faint">
                      {sf.score != null ? (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-text-secondary dark:bg-gray-800">
                          {sf.score}/5
                        </span>
                      ) : (
                        <span>unscored</span>
                      )}
                    </span>
                  </div>
                  {sf.evidence && (
                    <p className="mt-1 whitespace-pre-wrap text-[11px] text-text-secondary">
                      {sf.evidence}
                    </p>
                  )}
                  {sf.briefFieldValueExcerpt && (
                    <details className="mt-1 text-[10px] text-text-faint">
                      <summary className="cursor-pointer select-none">Brief excerpt</summary>
                      <p className="mt-1 whitespace-pre-wrap">{sf.briefFieldValueExcerpt}</p>
                    </details>
                  )}
                </div>
              ))}
              {assessment.structuralFitDimensionCoverage?.map((cov) => (
                <div
                  key={cov.briefFieldKey}
                  className="rounded border border-border/60 bg-card-hover/40 px-2 py-1.5"
                >
                  <p className="text-[11px] text-text-secondary">
                    <span
                      className="font-medium text-text-primary"
                      title={`Brief field: ${cov.briefFieldKey}`}
                    >
                      {cov.briefFieldLabel}
                    </span>{" "}
                    — scored under{" "}
                    <span className="text-text-secondary">
                      {cov.coveringDimensions.map((d) => d.name).join(", ")}
                    </span>{" "}
                    in Competency Scores.
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {(assessment.strengths || assessment.concerns) && (
          <div className="grid gap-3 sm:grid-cols-2">
            {assessment.strengths && (
              <div>
                <h5 className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  Strengths
                </h5>
                <p className="mt-1 whitespace-pre-wrap text-xs text-text-secondary">
                  {assessment.strengths}
                </p>
              </div>
            )}
            {assessment.concerns && (
              <div>
                <h5 className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  Concerns
                </h5>
                <p className="mt-1 whitespace-pre-wrap text-xs text-text-secondary">
                  {assessment.concerns}
                </p>
              </div>
            )}
          </div>
        )}

        {onViewDetails && (
          <button
            type="button"
            onClick={onViewDetails}
            className="text-[11px] font-medium text-accent hover:underline"
          >
            View full assessment
          </button>
        )}
      </div>
    </div>
  );
}
